import React, { useState, useEffect } from 'react';
import { Play as PlayIcon, RefreshCw, Loader2, X, Bot, Settings, Monitor, Smartphone, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PokerTable from '../components/PokerTable';
import PaywallDialog from '../components/PaywallDialog';
import { toast } from 'sonner';
import { sounds } from '../utils/sounds';

const Play = () => {
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [handFeedback, setHandFeedback] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [showDecisionPrompt, setShowDecisionPrompt] = useState(false);
  const [decisionPromptData, setDecisionPromptData] = useState(null);
  const [preferences, setPreferences] = useState({ 
    auto_hand_feedback: true, 
    show_decision_prompts: true 
  });
  const [isMobile, setIsMobile] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate('/');
    }
  };

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load user preferences on mount
  useEffect(() => {
    loadPreferences();
  }, []);

  // Auto-start game on mount (TASK 2: Remove Deal Hand screen)
  useEffect(() => {
    // Only auto-start if no game exists yet
    // Add small delay to ensure component is fully mounted and user is authenticated
    const autoStart = async () => {
      if (!game && !loading) {
        await new Promise(resolve => setTimeout(resolve, 500)); // Wait 500ms
        if (!game) { // Check again after delay
          startNewHand();
        }
      }
    };
    autoStart();
  }, []); // Run once on mount

  const loadPreferences = async () => {
    try {
      const response = await fetch(`${API_URL}/api/platform/preferences`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPreferences(data);
      }
    } catch (error) {
      console.error('Failed to load preferences:', error);
    }
  };

  const togglePreference = async (key, value) => {
    try {
      const response = await fetch(`${API_URL}/api/platform/preferences`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [key]: value })
      });
      if (response.ok) {
        const data = await response.json();
        setPreferences(data);
        toast.success('Settings updated');
      }
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const startNewHand = async () => {
    console.log('[START HAND] Button clicked, loading state:', loading);
    setLoading(true);
    try {
      console.log('[START HAND] Fetching from:', `${API_URL}/api/platform/game/start`);
      const response = await fetch(`${API_URL}/api/platform/game/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          player_count: 6,
          small_blind: 5,
          big_blind: 10
        })
      });

      console.log('[START HAND] Response status:', response.status);

      if (response.status === 403) {
        let errorDetail = 'Daily hand limit reached. Please upgrade.';
        try {
          const error = await response.json();
          errorDetail = error.detail || errorDetail;
          console.error('[START HAND] 403 Error:', error);
        } catch (parseError) {
          console.warn('[START HAND] Could not parse 403 response');
        }
        setShowPaywall(true);
        toast.error(errorDetail);
        return;
      }

      if (response.ok) {
        const data = await response.json();
        console.log('[START HAND] Got game data:', data.game?.game_id);
        setGame(data.game);  // STANDARDIZED: Always "game"
        toast.success('New hand dealt!');
        
        // Play dealing sounds (wrapped in try-catch to prevent blocking)
        try {
          setTimeout(() => sounds.playCardDeal(), 100);
          setTimeout(() => sounds.playCardDeal(), 250);
          setTimeout(() => sounds.playCardDeal(), 400);
        } catch (soundError) {
          console.warn('[START HAND] Sound error (non-blocking):', soundError);
        }
        
        // Skip auto decision prompt on initial auto-start to avoid errors
        // Random decision prompt (20% chance) - only if user manually started a new hand
        // if (preferences.show_decision_prompts && Math.random() < 0.2) {
        //   triggerDecisionPrompt(data.state);
        // }
      } else {
        const errorText = await response.text();
        console.error('[START HAND] Error response:', errorText);
        throw new Error('Failed to start game');
      }
    } catch (error) {
      console.error('[START HAND] Exception:', error);
      toast.error('Failed to start game. Please try again.');
    } finally {
      setLoading(false);
      console.log('[START HAND] Done, loading false');
    }
  };

  const takeAction = async (action, amount = 0) => {
    setActionInProgress(true);
    
    // Play action sounds
    if (action === 'fold') sounds.playClick();
    else if (action === 'raise' || action === 'bet') sounds.playChips();
    else sounds.playClick();
    
    try {
      const response = await fetch(`${API_URL}/api/platform/game/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action, amount })
      });

      if (response.ok) {
        const data = await response.json();
        setGame(data.game);  // STANDARDIZED: Always "game"

        // Show XP/level up notifications
        if (data.hand_complete && data.result?.xp_result) {
          const xpResult = data.result.xp_result;
          if (xpResult.level_up) {
            toast.success(`🎉 Level Up! You're now Level ${xpResult.level}!`, { duration: 4000 });
          }
          toast.info(`+${xpResult.xp_awarded} XP`, { duration: 2000 });
        }

        // Show new badges
        if (data.result?.new_badges && data.result.new_badges.length > 0) {
          toast.success(`🏆 New Badge Unlocked!`, { duration: 3000 });
        }

        // Show winner
        if (data.hand_complete && data.winners && data.winners.length > 0) {
          const winner = data.winners[0];
          
          // Play win sound for player
          if (winner.player_name === 'You') {
            sounds.playWin();
          }
          
          // Easter egg: "Kiss a Pig" for big wins (pot > $150)
          if (winner.player_name === 'You' && winner.winnings > 150) {
            toast.success(`🐷 KISS A PIG! You won $${winner.winnings} with ${winner.hand_rank}!`, { duration: 5000 });
          } else {
            toast.success(`${winner.player_name} wins $${winner.winnings} with ${winner.hand_rank}!`, { duration: 3000 });
          }
          
          // Trigger auto hand feedback if enabled
          if (preferences.auto_hand_feedback) {
            await getHandFeedback(data);
          }
        }
      } else {
        const error = await response.json();
        
        // Handle 404 - game lost due to backend restart  
        if (response.status === 404) {
          console.error('[GAME ACTION] 404 - Game session lost');
          toast.error('Game session lost. Starting a new hand...', { duration: 3000 });
          setGame(null);
          setTimeout(() => startNewHand(), 1500);
          return;
        }
        
        toast.error(error.detail || 'Action failed');
      }
    } catch (error) {
      toast.error('Failed to process action');
    } finally {
      setActionInProgress(false);
    }
  };

  const getHandFeedback = async (handData) => {
    if (!preferences.auto_hand_feedback) return;
    
    setFeedbackLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/platform/coach/analyze-hand`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          hand_summary: {
            player_hand: handData.player_hand || [],
            community_cards: handData.community_cards || [],
            actions_taken: handData.actions_taken || [],
            result: handData.winners ? 
              (handData.winners.some(w => w.player_name === 'You') ? 'won' : 'lost') : 
              'folded',
            pot_size: handData.pot_size || 0,
            winners: handData.winners || []
          }
        })
      });

      if (response.ok) {
        const data = await response.json();
        setHandFeedback(data.analysis);
        setShowFeedback(true);
      }
    } catch (error) {
      console.error('Failed to get hand feedback:', error);
    } finally {
      setFeedbackLoading(false);
    }
  };

  const triggerDecisionPrompt = (gameState) => {
    if (!preferences.show_decision_prompts) return;
    
    // Only trigger if it's the human player's turn
    const humanPlayerIndex = gameState.players.findIndex(p => p.is_human);
    if (humanPlayerIndex !== gameState.current_player_index) {
      return; // Not your turn, don't show prompt
    }
    
    // Create rich scenario description
    const humanPlayer = gameState.players[humanPlayerIndex];
    const potOdds = gameState.current_bet > 0 ? 
      `${Math.round((gameState.pot / gameState.current_bet) * 10) / 10}:1` : 
      'N/A';
    
    const prompt = {
      situation: `You're in ${humanPlayer.position || 'position'}. Pot: $${gameState.pot}. ${gameState.current_bet > 0 ? `Bet to call: $${gameState.current_bet}` : 'No bet'}. ${gameState.current_bet > 0 ? `Pot odds: ${potOdds}` : 'You can check'}. Stack: $${humanPlayer.chips}.`,
      question: "What would you do in this situation?",
      options: gameState.current_bet > 0 ? ['Fold', 'Call', 'Raise', 'All In'] : ['Check', 'Bet', 'Raise', 'All In'],
      timestamp: new Date().toISOString()
    };
    
    setDecisionPromptData(prompt);
    setShowDecisionPrompt(true);
    
    // Auto-dismiss after 20 seconds
    setTimeout(() => {
      setShowDecisionPrompt(false);
    }, 20000);
  };

  return (
    <div className="poker-play-container bg-gradient-to-b from-[#0B0F14] via-[#0A241C] to-[#071A14] h-screen overflow-hidden flex flex-col">
      <div className="poker-game-area max-w-[1400px] w-full mx-auto px-2 flex-1 flex flex-col min-h-0">
        
        {/* Compact top bar with back button and Deal Next Hand */}
        <div className="flex items-center justify-between mb-1">
          <button
            onClick={handleBack}
            className="flex items-center gap-1 text-foreground/60 hover:text-foreground text-xs"
            data-testid="back-button"
          >
            <ArrowLeft className="w-3 h-3" />
            <span>Back</span>
          </button>
          
          {/* Deal Next Hand Button - AT TOP */}
          {game && game.street === 'showdown' && (
            <button
              onClick={startNewHand}
              disabled={loading}
              className="px-6 py-2 bg-gradient-to-r from-[#C9A227] to-[#E1C25B] text-[#0B0F14] rounded-lg font-bold text-sm hover:shadow-lg"
            >
              {loading ? 'Dealing...' : '🔄 Deal Next Hand'}
            </button>
          )}
        </div>

        {/* Mobile Warning Banner */}
        {isMobile && (
          <div className="mb-6 p-4 bg-[#C9A227]/10 border border-[#C9A227]/30 rounded-lg backdrop-blur-sm">
            <div className="flex items-start gap-3">
              <Monitor className="w-5 h-5 text-[#C9A227] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-[#C9A227] mb-1">
                  ⚠️ Best Experienced on Desktop
                </p>
                <p className="text-xs text-foreground/70">
                  The poker table is optimized for larger screens. Mobile experience coming soon!
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Game Area - Flexible container that scales to fit */}
        {!game ? (
          <div className="flex flex-col items-center justify-center flex-1">
            {/* Real Poker Chips Photo */}
            <div className="mb-4">
              <img 
                src="https://images.unsplash.com/photo-1561102385-a6419d8a91c6?w=300&h=225&fit=crop&crop=center&q=80&auto=format" 
                alt="Poker chips"
                className="w-48 h-36 object-cover rounded-xl shadow-xl border border-[#C9A227]/20"
                loading="lazy"
              />
            </div>
            <h2 className="text-xl font-heading font-semibold mb-1.5">Ready to Play?</h2>
            <p className="text-foreground/60 mb-4 text-sm">Start a new hand and test your skills</p>
            <button
              onClick={startNewHand}
              disabled={loading}
              data-testid="start-hand-btn"
              className="px-8 py-4 bg-[#C9A227] text-[#0B0F14] rounded-lg font-heading font-bold text-lg hover:bg-[#E1C25B] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 shadow-lg hover:shadow-xl"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Dealing...
                </>
              ) : (
                <>
                  <PlayIcon className="w-5 h-5" />
                  Deal Hand
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="poker-table-container flex-1 flex flex-col min-h-0">
            {/* New Hand Button (after hand completes) - MOVED TO TOP */}
            {game.street === 'showdown' && (
              <div className="text-center mb-1">
                <button
                  onClick={startNewHand}
                  disabled={loading}
                  data-testid="new-hand-btn"
                  className="px-6 py-3 bg-[#C9A227] text-[#0B0F14] rounded-lg font-heading font-semibold hover:bg-[#E1C25B] transition-colors disabled:opacity-50 flex items-center gap-2 mx-auto"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <RefreshCw className="w-5 h-5" />
                  )}
                  Deal Next Hand
                </button>
              </div>
            )}

            {/* Dynamic HUD Status Bar - ABOVE TABLE */}
            {game && (
              <div 
                className={`
                  flex-shrink-0 flex items-center justify-between px-6 py-3
                  bg-gradient-to-r from-[#0A1628]/90 via-[#0D1B2A]/90 to-[#0A1628]/90 
                  backdrop-blur-sm rounded-xl shadow-lg mt-12 mb-6 mx-auto max-w-[800px]
                  transition-all duration-300 ease-in-out
                  ${game.players?.[game.current_player_index]?.is_human 
                    ? 'border-2 border-[#C9A227]/60 shadow-[0_0_25px_rgba(201,162,39,0.4)] animate-hud-pulse' 
                    : 'border border-[#1B4B7F]/40 opacity-75'
                  }
                `}
                data-testid="gameplay-status-hud"
              >
                <div className="flex items-center gap-8 text-sm">
                  {/* Bet Amount */}
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${game.players?.[game.current_player_index]?.is_human ? 'bg-[#C9A227] animate-pulse' : 'bg-blue-500'}`}></div>
                    <span className="text-blue-300/70 font-medium">Bet:</span>
                    <span className="text-white font-bold text-base">${game.current_bet?.toLocaleString()}</span>
                  </div>
                  
                  {/* Stack Amount */}
                  <div className="flex items-center gap-2">
                    <span className="text-blue-300/70 font-medium">Stack:</span>
                    <span className="text-emerald-400 font-bold text-base">${game.players?.find(p => p.is_human)?.chips?.toLocaleString()}</span>
                  </div>
                  
                  {/* Game Phase */}
                  <div className={`
                    text-xs uppercase tracking-widest font-bold px-3 py-1 rounded-full
                    ${game.players?.[game.current_player_index]?.is_human 
                      ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40' 
                      : 'bg-white/5 text-amber-400/70'
                    }
                  `}>
                    {game.street === 'preflop' ? 'PRE-FLOP' : game.street === 'flop' ? 'FLOP' : game.street === 'turn' ? 'TURN' : game.street === 'river' ? 'RIVER' : 'SHOWDOWN'}
                  </div>
                </div>
                
                {/* Your Turn Indicator */}
                {game.players?.[game.current_player_index]?.is_human && (
                  <div className="flex items-center gap-2 bg-gradient-to-r from-[#C9A227]/20 to-amber-600/20 px-4 py-2 rounded-full border border-[#C9A227]/40 animate-fade-in">
                    <div className="w-2 h-2 bg-[#C9A227] rounded-full animate-pulse"></div>
                    <span className="text-[#C9A227] font-bold text-xs tracking-wider uppercase">YOUR TURN</span>
                  </div>
                )}
              </div>
            )}
            
            {/* Poker Table wrapper - Centered with proper spacing */}
            <div className="flex-1 min-h-0 flex items-center justify-center py-1">
              <PokerTable
                gameState={game}
                onAction={takeAction}
                actionInProgress={actionInProgress}
              />
            </div>
          </div>
        )}
      </div>
      
      {/* Paywall Dialog */}
      <PaywallDialog 
        open={showPaywall} 
        onClose={() => setShowPaywall(false)} 
        trigger="hand_limit"
      />

      {/* Hand Feedback Modal */}
      {showFeedback && handFeedback && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" data-testid="feedback-modal">
          <div className="bg-[#0F1419] border border-[#C9A227]/30 rounded-2xl p-8 max-w-2xl w-full mx-4 shadow-2xl">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#C9A227]/20 flex items-center justify-center">
                  <Bot className="w-6 h-6 text-[#C9A227]" />
                </div>
                <div>
                  <h3 className="text-xl font-heading font-bold text-[#C9A227]">Hand Analysis</h3>
                  <p className="text-sm text-foreground/60">AI Coach Feedback</p>
                </div>
              </div>
              <button 
                onClick={() => setShowFeedback(false)}
                className="text-foreground/60 hover:text-foreground transition-colors"
                data-testid="close-feedback-btn"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="bg-[#121923] rounded-xl p-6 mb-6 border border-white/5">
              <div className="text-foreground/90 leading-relaxed space-y-4">
                {handFeedback.split('\n\n').map((paragraph, idx) => {
                  // Check if paragraph starts with a section label like [State Check], [Best Play], etc.
                  const labelMatch = paragraph.match(/^\[([^\]]+)\]\s*(.*)$/s);
                  
                  if (labelMatch) {
                    const [, label, content] = labelMatch;
                    return (
                      <div key={idx} className="space-y-1">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="h-1 w-1 rounded-full bg-[#C9A227]"></div>
                          <h4 className="text-[#C9A227] font-semibold text-sm uppercase tracking-wide">
                            {label}
                          </h4>
                        </div>
                        <p className="text-foreground/90 pl-4 whitespace-pre-wrap">{content.trim()}</p>
                      </div>
                    );
                  }
                  
                  // Regular paragraph without label
                  return (
                    <p key={idx} className="whitespace-pre-wrap">{paragraph}</p>
                  );
                })}
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowFeedback(false)}
                className="flex-1 bg-[#C9A227] hover:bg-[#C9A227]/90 text-[#0F1419] font-semibold py-3 rounded-xl transition-colors"
                data-testid="feedback-got-it-btn"
              >
                Got it, thanks!
              </button>
              <button
                onClick={() => {
                  setShowFeedback(false);
                  togglePreference('auto_hand_feedback', false);
                }}
                className="px-4 py-3 border border-white/10 hover:border-[#C9A227]/50 rounded-xl transition-colors text-sm"
                data-testid="disable-auto-feedback-btn"
              >
                Don't show again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Decision Prompt Modal - "What would you do?" - MODERN INTERACTIVE */}
      {showDecisionPrompt && decisionPromptData && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4" data-testid="decision-prompt-modal">
          <div className="bg-gradient-to-br from-[#0F1419] to-[#0B0F14] border-2 border-[#C9A227] rounded-2xl p-8 max-w-3xl w-full shadow-2xl">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-[#C9A227] to-[#E1C25B] mb-4 shadow-lg">
                <span className="text-4xl">🤔</span>
              </div>
              <h3 className="text-3xl font-heading font-bold text-[#C9A227] mb-4">What Would You Do?</h3>
              
              {/* Situation Cards */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-[#121923] border border-[#C9A227]/20 rounded-xl p-4">
                  <div className="text-[#C9A227] text-2xl font-bold mb-1">${game?.pot || 0}</div>
                  <div className="text-xs text-foreground/60 uppercase tracking-wide">Pot Size</div>
                </div>
                <div className="bg-[#121923] border border-[#C9A227]/20 rounded-xl p-4">
                  <div className="text-white text-2xl font-bold mb-1">${game?.current_bet || 0}</div>
                  <div className="text-xs text-foreground/60 uppercase tracking-wide">To Call</div>
                </div>
              </div>
              
              <p className="text-foreground/70 text-sm leading-relaxed">{decisionPromptData.situation}</p>
            </div>
            
            {/* INTERACTIVE ACTION CARDS - Multiple Choice */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              {decisionPromptData.options.map((option, idx) => {
                // Define visual style per action
                const actionStyles = {
                  'Fold': { icon: '🚫', color: 'red', desc: 'Give up your hand' },
                  'Check': { icon: '✋', color: 'blue', desc: 'No bet, stay in' },
                  'Call': { icon: '🤝', color: 'green', desc: `Match $${game?.current_bet || 0}` },
                  'Bet': { icon: '💰', color: 'yellow', desc: 'Put chips in the pot' },
                  'Raise': { icon: '🚀', color: 'purple', desc: 'Increase the bet' },
                  'All In': { icon: '🔥', color: 'orange', desc: 'Bet all your chips' }
                };
                
                const style = actionStyles[option] || { icon: '🎯', color: 'gray', desc: 'Make your move' };
                
                return (
                  <button
                    key={idx}
                    onClick={() => {
                      toast.success(`You chose: ${option}! Now execute it below.`, { duration: 2500 });
                      setShowDecisionPrompt(false);
                    }}
                    className={`
                      group relative bg-[#121923] hover:bg-[#1A2332] border-2 border-white/10 
                      hover:border-${style.color}-500/50 hover:shadow-lg hover:shadow-${style.color}-500/20
                      rounded-xl p-6 transition-all transform hover:scale-105 active:scale-95
                    `}
                    data-testid={`decision-option-${option.toLowerCase()}`}
                  >
                    {/* Icon */}
                    <div className="text-5xl mb-3 group-hover:scale-110 transition-transform">
                      {style.icon}
                    </div>
                    
                    {/* Action Name */}
                    <div className="text-xl font-heading font-bold text-foreground mb-2 group-hover:text-[#C9A227] transition-colors">
                      {option}
                    </div>
                    
                    {/* Description */}
                    <div className="text-xs text-foreground/60 group-hover:text-foreground/80 transition-colors">
                      {style.desc}
                    </div>
                    
                    {/* Hover indicator */}
                    <div className="absolute top-2 right-2 w-3 h-3 rounded-full bg-[#C9A227] opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                );
              })}
            </div>
            
            {/* Skip button */}
            <button
              onClick={() => setShowDecisionPrompt(false)}
              className="w-full py-3 text-sm text-foreground/50 hover:text-foreground/80 hover:bg-white/5 rounded-lg transition-all"
              data-testid="skip-decision-prompt-btn"
            >
              Skip this challenge →
            </button>
          </div>
        </div>
      )}

      {/* Settings Panel - Floating Button */}
      <div className="fixed bottom-6 left-6 z-40">
        <div className="relative">
          <button
            onClick={(e) => {
              e.stopPropagation();
              const dropdown = document.getElementById('settings-dropdown');
              dropdown?.classList.toggle('hidden');
            }}
            className="w-12 h-12 rounded-full bg-[#1A2332] border border-white/10 hover:border-[#C9A227]/50 flex items-center justify-center transition-all hover:scale-110 shadow-lg"
            data-testid="settings-btn"
          >
            <Settings className="w-5 h-5 text-foreground/60" />
          </button>
          
          {/* Settings Dropdown */}
          <div 
            id="settings-dropdown"
            className="hidden absolute bottom-full left-0 mb-2 w-72 bg-[#0F1419] border border-white/10 rounded-xl p-4 shadow-2xl"
            data-testid="settings-dropdown"
          >
            <h4 className="text-sm font-semibold mb-3 text-[#C9A227]">Coach Settings</h4>
            
            <div className="space-y-3">
              {/* Auto Feedback Toggle */}
              <label className="flex items-center justify-between cursor-pointer group">
                <div>
                  <div className="text-sm text-foreground/80 group-hover:text-foreground transition-colors">
                    Auto Hand Feedback
                  </div>
                  <div className="text-xs text-foreground/50">Get AI analysis after each hand</div>
                </div>
                <button
                  onClick={() => togglePreference('auto_hand_feedback', !preferences.auto_hand_feedback)}
                  className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
                    preferences.auto_hand_feedback ? 'bg-[#C9A227]' : 'bg-white/10'
                  }`}
                  data-testid="toggle-auto-feedback"
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                    preferences.auto_hand_feedback ? 'translate-x-5' : ''
                  }`} />
                </button>
              </label>
              
              {/* Decision Prompts Toggle */}
              <label className="flex items-center justify-between cursor-pointer group">
                <div>
                  <div className="text-sm text-foreground/80 group-hover:text-foreground transition-colors">
                    Decision Prompts
                  </div>
                  <div className="text-xs text-foreground/50">"What would you do?" challenges</div>
                </div>
                <button
                  onClick={() => togglePreference('show_decision_prompts', !preferences.show_decision_prompts)}
                  className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
                    preferences.show_decision_prompts ? 'bg-[#C9A227]' : 'bg-white/10'
                  }`}
                  data-testid="toggle-decision-prompts"
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                    preferences.show_decision_prompts ? 'translate-x-5' : ''
                  }`} />
                </button>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Play;
